import React, { useRef, useState } from 'react';
import { UploadIcon } from 'lucide-react';
import { BUTTON_QUIET } from './adminStyles';
import { extractDocxText, isDocx, isLegacyDoc } from '../../utils/docx';
export interface UploadOutcome {
  ok: boolean;
  error?: string;
}
interface TranscriptUploadProps {
  /** Current transcript text, if one has been stored. */
  value: string;
  /** The source document this transcript came from. */
  filename?: string;
  /** Word count of that document. */
  wordCount?: number;
  /**
   * True while the deliverable it belongs to does not exist yet, so the text
   * cannot be stored until the row is created. Stated plainly rather than
   * shown as an upload that has already been saved.
   */
  pending?: boolean;
  /**
   * Stores the parsed transcript. The upload is only reported as complete once
   * this resolves ok, so a file that was parsed but not stored never looks
   * like one that was.
   */
  onUpload: (text: string, filename: string, words: number) => Promise<UploadOutcome>;
}
function countWords(text: string): number {
  return text.trim() ? text.trim().split(/\s+/).length : 0;
}

/**
 * Transcripts are written elsewhere (usually Descript) and uploaded here.
 *
 * A .docx has its readable text extracted; .txt, .vtt and .srt are read as-is.
 * Either way the text is stored immediately in transcript_versions — it never
 * sits in this browser waiting to be saved.
 */
export function TranscriptUpload({
  value,
  filename,
  wordCount,
  pending = false,
  onUpload
}: TranscriptUploadProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState('');
  const words = wordCount ?? countWords(value);
  const stored = Boolean(value.trim());
  async function read(file: File | undefined) {
    if (!file) return;
    setFailed('');
    if (isLegacyDoc(file)) {
      setFailed('Word .doc files cannot be read. Save it as .docx and try again.');
      return;
    }
    setBusy(true);
    try {
      const text = isDocx(file) ? await extractDocxText(file) : await file.text();
      if (!text.trim()) {
        setFailed(isDocx(file) ? 'No text could be read from that Word file.' : 'That file is empty.');
        return;
      }

      /* Stored before it is shown as uploaded. */
      const result = await onUpload(text, file.name, countWords(text));
      if (!result.ok) {
        setFailed(result.error ?? 'The transcript could not be saved. Nothing was stored.');
      }
    } catch (error) {
      console.error('[transcript] file could not be read', error);
      setFailed(isDocx(file) ? 'That Word file could not be read. Check the file and try again.' : 'That file could not be read. Check the file and try again.');
    } finally {
      setBusy(false);
    }
  }
  return <div className="mt-8" data-id="element-396" data-mp="35ikvbwzw039d">
      <span className="label block text-paper/40" data-id="element-397" data-mp="30lu5wj26zvba">Transcript File</span>
      <div className="mt-4 flex flex-wrap items-center gap-6 border-b border-paper/25 pb-4" data-id="element-398" data-mp="30jhny5wfpky8">
        <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className={BUTTON_QUIET} data-id="element-399" data-mp="382ze4246p93r">
          <span className="flex items-center gap-3" data-id="element-400" data-mp="3hvy49ofbx7ph">
            <UploadIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-401" data-mp="3mtzx312k4le5" />
            {busy ? 'Saving…' : stored ? 'Replace File' : 'Upload File'}
          </span>
        </button>
        <p className="label-sm m-0 text-paper/30" data-id="element-402" data-mp="2p826shhxrbrn">
          {stored ? `${filename ? `${filename} · ` : ''}${words.toLocaleString()} words${pending ? ' · stores when added' : ''}` : '.docx, .txt or .vtt'}
        </p>
      </div>

      {failed ? <p role="alert" className="label-sm mt-4 text-paper/60" data-id="element-403" data-mp="333w74c4n8st5">
          {failed}
        </p> : null}

      <input ref={fileRef} type="file" accept=".docx,.txt,.vtt,.srt,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={event => {
      void read(event.target.files?.[0]);
      event.target.value = '';
    }} className="hidden" data-id="element-404" data-mp="egiumoav504v" />
    </div>;
}